import { Card, CardContent, Skeleton, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import {
  Button,
  InformationModal,
  ItemImageArea,
  SkeletonText,
} from 'utoniq-ui'

import {
  Item,
  ItemBoxFragment,
  Maybe,
  PickupUserDetailFragment,
  PickupUserFragment,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ExchangeCategory } from '@/components/pages/ConsoleTicketReceives/template'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  artistId: string
  pickupUser: Maybe<PickupUserDetailFragment>
  items: PickupUserFragment['userItems']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onUseTickets: () => Promise<void>
  onUnuseTickets: () => Promise<void>
}

export const ConsoleTicketReceiveDetailTemplate: Template<State, Action> = ({
  state,
  onUseTickets,
  onUnuseTickets,
}) => {
  const { t } = useTranslation('consoleTicketReceiveDetail')
  const { routerNames } = useRouter()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleTicketReceiveDetail} />
      <ConsoleBaseLayout margin header={{ back: true, title: t('title') }}>
        {state.loading ? (
          <>
            <TicketDetailSkeleton />
            <UserInfoSkeleton />
          </>
        ) : (
          <>
            <TicketDetailCell
              pickupUser={state.pickupUser}
              items={state.items}
              onUseTickets={onUseTickets}
              onUnuseTickets={onUnuseTickets}
            />
            <UserInfoCell user={state.pickupUser?.user} />
          </>
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type TicketDetailCellProps = {
  pickupUser: State['pickupUser']
  items: State['items']
  onUseTickets: Action['onUseTickets']
  onUnuseTickets: Action['onUnuseTickets']
}
const TicketDetailCell: Cell<TicketDetailCellProps> = ({
  pickupUser,
  items,
  onUseTickets,
  onUnuseTickets,
}) => {
  const { t } = useTranslation('consoleTicketReceiveDetail')
  const [loading, setLoading] = useState<boolean>(false)
  const [openUseConfirm, setOpenUseConfirm] = useState<boolean>(false)
  const [openUnuseConfirm, setOpenUnuseConfirm] = useState<boolean>(false)

  return (
    <CellFrame sx={{ pt: 2, pb: 5 }}>
      <Typography variant="h3" sx={{ py: 4 }}>
        {t('ticketIHave', '持っているチケット')}
      </Typography>
      <Card elevation={0}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack spacing={1} sx={{ pb: 3 }}>
            <ExchangeCategory isUsed={!!pickupUser?.isCompleted} />
            <Typography variant="subtitle1">
              {t('receiptDate', '受け取り日時：')}
            </Typography>
            <Typography variant="subtitle1">
              {pickupUser?.formattedPickupStartEndTime}
            </Typography>
          </Stack>
          <Typography variant="h5">{pickupUser?.user.fullName}</Typography>
          <Typography variant="h6">
            <small>{t('accountName', 'アカウント名')}:</small>{' '}
            {pickupUser?.user.displayName}
          </Typography>
          <Typography variant="h6" sx={{ pb: 2, pt: 4 }}>
            {pickupUser?.collection.title}
          </Typography>
          <Stack spacing={4} sx={{ pb: 4 }}>
            {items?.map((item, index) => {
              const order = pickupUser?.pickupOrder.find(
                ({ itemId }) => itemId === item.id
              )

              const quantity = item.item.sku
                ? order?.serials.filter(
                    ({ skuId }) => skuId === item.item.sku?.id
                  ).length
                : order?.count

              if (!quantity) return null

              return (
                <OwnedTicketItemCell
                  item={item.item}
                  quantity={quantity}
                  key={index}
                />
              )
            })}
          </Stack>
          {!pickupUser?.isCompleted ? (
            <Button
              fullWidth
              size="large"
              onClick={() => setOpenUseConfirm(true)}
            >
              {t('markUsed', '使用済みにする')}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => setOpenUnuseConfirm(true)}
              sx={{ color: 'primary.main', borderColor: 'primary.main' }}
            >
              {t('leaveUnused', '未使用にする')}
            </Button>
          )}
        </CardContent>
      </Card>
      <InformationModal
        open={openUseConfirm}
        title={t('confirmMarkUserTitle')}
        content=""
        onClose={() => setOpenUseConfirm(false)}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            loading={loading}
            disabled={loading}
            onClick={async () => {
              setLoading(true)
              await onUseTickets()
              setOpenUseConfirm(false)
              setLoading(false)
            }}
          >
            {t('change')}
          </Button>
        }
        sx={{
          // 単に props で指定するだけだと
          // default のスタイルが優先されるため
          // セレクタを用いた CSS 指定を行っている
          '& .MuiBackdrop-root': {
            // 画面幅に依らずバックドロップを画面全体にかける
            maxWidth: '100%',
          },
        }}
      />
      <InformationModal
        open={openUnuseConfirm}
        title={t('confirmUnMarkUserTitle')}
        content=""
        onClose={() => setOpenUnuseConfirm(false)}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            loading={loading}
            disabled={loading}
            onClick={async () => {
              setLoading(true)
              await onUnuseTickets()
              setOpenUnuseConfirm(false)
              setLoading(false)
            }}
          >
            {t('change')}
          </Button>
        }
        sx={{
          // 単に props で指定するだけだと
          // default のスタイルが優先されるため
          // セレクタを用いた CSS 指定を行っている
          '& .MuiBackdrop-root': {
            // 画面幅に依らずバックドロップを画面全体にかける
            maxWidth: '100%',
          },
        }}
      />
    </CellFrame>
  )
}

const TicketDetailSkeleton: Cell = () => {
  return (
    <CellFrame sx={{ pt: 2, pb: 5 }}>
      <Stack sx={{ py: 4 }}>
        <SkeletonText variant="large" width="75%" />
      </Stack>
      <Card elevation={0}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack direction="row" sx={{ pb: 3 }}>
            <Stack flex={1}>
              <SkeletonText variant="large" width="100%" />
            </Stack>
            <Stack flex={5}>
              <SkeletonText variant="large" width="75%" />
            </Stack>
          </Stack>
          <Stack sx={{ pb: 2 }}>
            <SkeletonText variant="h3" width="60%" />
          </Stack>
          <Stack spacing={4} sx={{ pb: 4 }}>
            <Stack direction="row" spacing={3}>
              <Skeleton variant="rectangular" width={60} height={96} />
              <Stack flex={1} alignItems="flex-end">
                <SkeletonText variant="large" />
                <SkeletonText variant="small" />
                <SkeletonText variant="h3" width="40%" />
              </Stack>
            </Stack>
          </Stack>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={44}
            sx={{ borderRadius: 34 }}
          />
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type UserInfoCellProps = {
  user?: PickupUserDetailFragment['user']
}
const UserInfoCell: Cell<UserInfoCellProps> = ({ user }) => {
  const { t } = useTranslation('consoleTicketReceiveDetail')
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h3" sx={{ pb: 3 }}>
        {t('customerInfo', 'お客様情報')}
      </Typography>
      <Card
        elevation={0}
        sx={{
          bgcolor: 'transparent',
          borderColor: 'grey.200',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <CardContent sx={{ py: 4, px: 20, '&:last-child': { pb: 4 } }}>
          <Stack spacing={1} sx={{ pt: 1, pb: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {t('shippingAddress', '住所')}
            </Typography>
            <Typography variant="body2">
              {user?.address?.fullAddress}
            </Typography>
          </Stack>
          <Stack spacing={1} sx={{ pt: 1, pb: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {t('phoneNumber', '電話番号')}
            </Typography>
            <Typography variant="body2">
              {user?.formattedPhoneNumber}
            </Typography>
          </Stack>
          <Stack spacing={1} sx={{ pt: 1, pb: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {t('email', 'メールアドレス')}
            </Typography>
            <Typography variant="body2">{user?.email}</Typography>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

const UserInfoSkeleton: Cell = () => (
  <CellFrame>
    <Stack sx={{ pb: 3 }}>
      <SkeletonText variant="large" width="75%" />
    </Stack>
    <Card
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        borderColor: 'grey.200',
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    >
      <CardContent sx={{ py: 4, px: 20, '&:last-child': { pb: 4 } }}>
        <Stack spacing={1} sx={{ pt: 1, pb: 2 }}>
          <SkeletonText variant="body2" width="30%" />
          <SkeletonText variant="body2" width="100%" />
        </Stack>
        <Stack spacing={1} sx={{ pt: 1, pb: 2 }}>
          <SkeletonText variant="body2" width="30%" />
          <SkeletonText variant="body2" width="100%" />
        </Stack>
        <Stack spacing={1} sx={{ pt: 1, pb: 2 }}>
          <SkeletonText variant="body2" width="30%" />
          <SkeletonText variant="body2" width="100%" />
        </Stack>
      </CardContent>
    </Card>
  </CellFrame>
)

type OwnedTicketItemCellProps = {
  item: Partial<Item> | ItemBoxFragment
  quantity: number
}
const OwnedTicketItemCell: Cell<OwnedTicketItemCellProps> = ({
  item,
  quantity,
}) => {
  const { t } = useTranslation('consoleTicketReceiveDetail')
  const formattedPrice = useMemo(() => {
    if (item?.sku?.price) {
      return item?.sku?.formattedPrice ?? '0'
    }
    return item?.formattedPrice ?? '0'
  }, [item?.formattedPrice, item?.sku?.formattedPrice, item?.sku?.price])

  return (
    <Stack direction="row" spacing={3} sx={{ pb: 4 }}>
      {item.thumbnail?.front?.mediumUri ? (
        <ItemImageArea size="small" src={item.thumbnail.front.mediumUri} />
      ) : (
        <Skeleton variant="rectangular" width={60} height={96} />
      )}
      <Stack flex={1} spacing={1} sx={{ minWidth: 0 }}>
        <Stack>
          <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
            {item.title}
          </Typography>
          {!!item.sku && (
            <Typography variant="subtitle1">{item.sku?.unitName}</Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          <Typography variant="subtitle1">
            {t('itemPrice', '¥', { price: formattedPrice })}
          </Typography>
          <Typography variant="subtitle1">
            {t('itemQuantity', '数量', { quantity })}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}
